import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellRing, Clock, Check, CheckCheck, Megaphone,
  AlertTriangle, Bell, X, RefreshCw,
  AlertCircle, ChevronDown, Trash2, Calendar,
} from 'lucide-react';
import SimpleBar from 'simplebar-react';
import { bellNotificationService } from '../../../modules/common-modules/Notification/services/bellNotification.service';
import logger from '../../utils/logger';
import { showToast } from './CustomToast';

interface BellNotification {
  id: string;
  title: string;
  message: string;
  category: string;
  priority: string;
  isRead: boolean;
  readAt?: string;
  actionUrl?: string;
  referenceType?: string;
  referenceId?: string;
  senderName?: string;
  senderAvatar?: string;
  createdAt: string;
  icon?: string;
  iconColor?: string;
}

interface NotificationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const categoryIcons: Record<string, React.ReactNode> = {
  announcement: <Megaphone className="size-4 text-blue-500" />,
  approval: <Check className="size-4 text-teal-500" />,
  reminder: <Calendar className="size-4 text-indigo-500" />,
  alert: <AlertTriangle className="size-4 text-red-500" />,
  system: <Bell className="size-4 text-gray-500" />,
};

const categoryLabels: Record<string, string> = {
  all: 'All',
  announcement: 'Announcements',
  approval: 'Approvals',
  reminder: 'Reminders',
  alert: 'Alerts',
  system: 'System',
};

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-blue-500',
  low: 'bg-gray-400',
};

const NotificationSidebar: React.FC<NotificationSidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<BellNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [readFilter, setReadFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 20;

  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      setError(null);

      const params: any = { page: pageNum, limit, channel: 'web' };
      if (activeFilter !== 'all') params.category = activeFilter;
      if (readFilter === 'unread') params.isRead = false;
      if (readFilter === 'read') params.isRead = true;

      const response = await bellNotificationService.getNotifications(params);

      if (response) {
        const items = response.data || [];
        if (append) {
          setNotifications(prev => [...prev, ...items]);
        } else {
          setNotifications(items);
        }
        setTotal(response.total || 0);
        setUnreadCount(items.filter(n => !n.isRead).length);
        setHasMore(items.length >= limit);
      }
    } catch (err) {
      logger.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, readFilter]);

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      fetchNotifications(1);
    }
  }, [isOpen, fetchNotifications]);

  // Polling when sidebar is open
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => fetchNotifications(1), 30000);
    return () => clearInterval(interval);
  }, [isOpen, fetchNotifications]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage, true);
  };

  const handleNotificationClick = async (notification: BellNotification) => {
    try {
      if (!notification.isRead) {
        await bellNotificationService.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
        onClose();
      }
    } catch (error) {
      logger.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setActionLoading('markAll');
      await bellNotificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      showToast.success('All notifications marked as read');
    } catch (err) {
      logger.error('Failed to mark all as read:', err);
      showToast.error('Failed to mark all as read');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setActionLoading(id);
      await bellNotificationService.dismiss(id);
      const notification = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notification && !notification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      logger.error('Failed to dismiss notification:', err);
      showToast.error('Failed to dismiss');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismissAll = async () => {
    try {
      setActionLoading('dismissAll');
      await bellNotificationService.dismissAll();
      setNotifications([]);
      setUnreadCount(0);
      showToast.success('All notifications cleared');
    } catch (err) {
      logger.error('Failed to dismiss all:', err);
      showToast.error('Failed to clear notifications');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-[1049] transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[400px] max-w-[90vw] bg-white dark:bg-zink-700 shadow-2xl z-[1050] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
          <div className="flex items-center gap-2">
            <BellRing className="size-5 text-custom-500" />
            <h5 className="text-16 font-semibold">Notifications</h5>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-[11px] font-medium rounded-full text-white bg-custom-500">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={actionLoading === 'markAll'}
                className="p-1.5 text-slate-500 hover:text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 rounded transition-colors"
                title="Mark all as read"
              >
                <CheckCheck className="size-4" />
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={handleDismissAll}
                disabled={actionLoading === 'dismissAll'}
                className="p-1.5 text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
                title="Clear all"
              >
                <Trash2 className="size-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-zink-200 hover:bg-slate-100 dark:hover:bg-zink-600 rounded transition-colors"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        {/* Read Filter Tabs */}
        <div className="flex border-b border-slate-200 dark:border-zink-500">
          {(['all', 'unread', 'read'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => { setReadFilter(filter); setPage(1); }}
              className={`flex-1 py-2.5 text-xs font-medium capitalize transition-colors ${
                readFilter === filter
                  ? 'text-custom-500 border-b-2 border-custom-500'
                  : 'text-slate-500 dark:text-zink-300 hover:text-slate-700'
              }`}
            >
              {filter}
              {filter === 'unread' && unreadCount > 0 && (
                <span className="ml-1 text-[10px]">({unreadCount})</span>
              )}
            </button>
          ))}
        </div>

        {/* Category Filters */}
        <div className="p-2 border-b border-slate-100 dark:border-zink-600">
          <div className="flex flex-wrap gap-1">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => { setActiveFilter(key); setPage(1); }}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors ${
                  activeFilter === key
                    ? 'bg-custom-500 text-white'
                    : 'bg-slate-100 dark:bg-zink-600 text-slate-600 dark:text-zink-300 hover:bg-slate-200 dark:hover:bg-zink-500'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Notification List */}
        <SimpleBar className="h-[calc(100vh-220px)]">
          {loading && notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-zink-300">
              <RefreshCw className="size-8 mx-auto mb-3 animate-spin opacity-40" />
              <p className="text-sm">Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <AlertCircle className="size-12 mx-auto mb-3 text-red-400" />
              <p className="text-sm text-red-500 mb-3">{error}</p>
              <button
                onClick={() => fetchNotifications(1)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 rounded transition-colors"
              >
                <RefreshCw className="size-3" />
                Retry
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-zink-300">
              <BellRing className="size-16 mx-auto mb-4 opacity-20" />
              <p className="text-sm font-medium mb-1">No notifications</p>
              <p className="text-xs opacity-70">
                {activeFilter !== 'all' || readFilter !== 'all'
                  ? 'Try changing your filters'
                  : "You're all caught up!"}
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100 dark:divide-zink-600">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`group relative flex gap-3 p-3.5 hover:bg-slate-50 dark:hover:bg-zink-600/50 transition-colors cursor-pointer ${
                      !notification.isRead ? 'bg-custom-50/30 dark:bg-custom-500/5' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="relative shrink-0">
                      <div className="flex items-center justify-center size-9 rounded-lg bg-slate-100 dark:bg-zink-600">
                        {categoryIcons[notification.category] || categoryIcons.system}
                      </div>
                      {!notification.isRead && (
                        <div className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${priorityColors[notification.priority] || priorityColors.medium}`} />
                      )}
                    </div>

                    <div className="grow min-w-0">
                      <h6 className={`text-[13px] leading-tight mb-1 ${!notification.isRead ? 'font-semibold text-slate-800 dark:text-zink-50' : 'font-medium text-slate-600 dark:text-zink-200'}`}>
                        {notification.title}
                      </h6>
                      <p className="text-xs text-slate-500 dark:text-zink-400 line-clamp-2 mb-1.5">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 text-[11px] text-slate-400 dark:text-zink-500">
                        <Clock className="size-3" />
                        <span>{formatTime(notification.createdAt)}</span>
                        {notification.senderName && (
                          <>
                            <span className="text-slate-300">|</span>
                            <span>{notification.senderName}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0 self-start pt-1">
                      <button
                        onClick={(e) => handleDismiss(notification.id, e)}
                        disabled={actionLoading === notification.id}
                        className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded opacity-0 group-hover:opacity-100 transition-all"
                        title="Dismiss"
                      >
                        <X className={`size-3.5 ${actionLoading === notification.id ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="p-3 text-center">
                  <button
                    onClick={loadMore}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 rounded-md transition-colors"
                  >
                    <ChevronDown className="size-3.5" />
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </SimpleBar>
      </div>
    </>
  );
};

export default NotificationSidebar;
